import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';

type CropShape = 'rect' | 'round';

interface ImageCropModalProps {
    isOpen: boolean;
    file: File | null;
    onCancel: () => void;
    onConfirm: (file: File) => Promise<void> | void;
    title?: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    aspect?: number;
    targetSize?: number | { width: number; height: number };
    outputFileName?: string;
    outputMimeType?: string;
    cropShape?: CropShape;
    zoomRange?: {
        min?: number;
        max?: number;
        step?: number;
    };
    initialZoom?: number;
}

interface Size {
    width: number;
    height: number;
}

const DEFAULT_SIZE: Size = { width: 512, height: 512 };
const DEFAULT_ZOOM_RANGE = { min: 0.1, max: 3, step: 0.01 };
const SUPPORTED_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = (err) => reject(err);
        image.src = src;
    });

const normalizeMimeType = (rawMimeType?: string): { mimeType: string; quality?: number } => {
    if (!rawMimeType) {
        return { mimeType: 'image/png' };
    }

    const lower = rawMimeType.toLowerCase();

    if (lower === 'image/jpg') {
        return { mimeType: 'image/jpeg', quality: 0.92 };
    }

    if (SUPPORTED_MIME_TYPES.has(lower)) {
        if (lower === 'image/jpeg') {
            return { mimeType: 'image/jpeg', quality: 0.92 };
        }
        return { mimeType: lower };
    }

    // Fallback to PNG for formats not supported by canvas.toBlob (e.g., SVG)
    return { mimeType: 'image/png' };
};

const getExtensionForMimeType = (mimeType: string) => {
    switch (mimeType) {
        case 'image/jpeg':
            return 'jpg';
        case 'image/png':
            return 'png';
        case 'image/webp':
            return 'webp';
        default:
            return 'png';
    }
};

const ensureFileNameHasExtension = (fileName: string | undefined, mimeType: string) => {
    const safeName = fileName?.trim() || 'image';
    if (safeName.includes('.')) {
        return safeName;
    }
    return `${safeName}.${getExtensionForMimeType(mimeType)}`;
};

const getCroppedBlob = async (
    imageSrc: string,
    crop: Area,
    outputSize: Size,
    mimeType: string,
    quality?: number
): Promise<Blob> => {
    const image = await loadImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = crop.width;
    canvas.height = crop.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas context não disponível.');
    }

    ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height
    );

    const needsResize = crop.width !== outputSize.width || crop.height !== outputSize.height;
    const targetCanvas = needsResize ? document.createElement('canvas') : canvas;

    if (needsResize) {
        targetCanvas.width = outputSize.width;
        targetCanvas.height = outputSize.height;
        const targetCtx = targetCanvas.getContext('2d');
        if (!targetCtx) {
            throw new Error('Canvas context não disponível.');
        }
        targetCtx.drawImage(canvas, 0, 0, outputSize.width, outputSize.height);
    }

    return new Promise<Blob>((resolve, reject) => {
        targetCanvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Falha ao gerar imagem recortada.'));
                    return;
                }
                resolve(blob);
            },
            mimeType,
            quality
        );
    });
};

const resolveOutputSize = (size?: number | Size): Size => {
    if (!size) return DEFAULT_SIZE;
    if (typeof size === 'number') {
        return { width: size, height: size };
    }
    return size;
};

const ImageCropModal: React.FC<ImageCropModalProps> = ({
    isOpen,
    file,
    onCancel,
    onConfirm,
    title = 'Ajustar imagem',
    description,
    confirmLabel = 'Aplicar recorte',
    cancelLabel = 'Cancelar',
    aspect,
    targetSize,
    outputFileName,
    outputMimeType,
    cropShape = 'rect',
    zoomRange,
    initialZoom = DEFAULT_ZOOM_RANGE.min,
}) => {
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(initialZoom);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const effectiveSize = useMemo(() => resolveOutputSize(targetSize), [targetSize]);
    const effectiveAspect = useMemo(() => {
        if (aspect) return aspect;
        return effectiveSize.width / effectiveSize.height;
    }, [aspect, effectiveSize.height, effectiveSize.width]);

    const effectiveZoomRange = useMemo(
        () => ({
            min: zoomRange?.min ?? DEFAULT_ZOOM_RANGE.min,
            max: zoomRange?.max ?? DEFAULT_ZOOM_RANGE.max,
            step: zoomRange?.step ?? DEFAULT_ZOOM_RANGE.step,
        }),
        [zoomRange]
    );

    const clampZoomValue = useCallback(
        (value: number) => {
            const { min, max } = effectiveZoomRange;
            if (Number.isNaN(value)) return min;
            return Math.min(Math.max(value, min), max);
        },
        [effectiveZoomRange]
    );

    useEffect(() => {
        setZoom((current) => clampZoomValue(current));
    }, [clampZoomValue]);

    useEffect(() => {
        if (!file) {
            setImageSrc(null);
            return;
        }

        setError(null);
        setCrop({ x: 0, y: 0 });
        setZoom(clampZoomValue(initialZoom));
        setCroppedAreaPixels(null);

        let isCancelled = false;
        const reader = new FileReader();
        reader.onload = () => {
            if (!isCancelled) {
                setImageSrc(reader.result as string);
            }
        };
        reader.onerror = () => {
            if (!isCancelled) {
                setError('Não foi possível carregar a imagem selecionada.');
            }
        };
        reader.readAsDataURL(file);

        return () => {
            isCancelled = true;
            reader.abort?.();
        };
    }, [file, initialZoom, clampZoomValue]);

    const handleCropComplete = useCallback((_: Area, croppedPixels: Area) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleConfirm = async () => {
        if (!file || !imageSrc || !croppedAreaPixels) {
            setError('Selecione e ajuste uma imagem antes de confirmar.');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            const { mimeType, quality } = normalizeMimeType(outputMimeType ?? file.type);
            const finalFileName = ensureFileNameHasExtension(outputFileName ?? file.name, mimeType);
            const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, effectiveSize, mimeType, quality);
            const croppedFile = new File([blob], finalFileName, { type: mimeType });
            await onConfirm(croppedFile);
        } catch (cropError: any) {
            console.error('Erro ao aplicar recorte da imagem:', cropError);
            const message =
                cropError?.message || 'Não foi possível aplicar o recorte. Tente novamente.';
            setError(message);
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen || !file) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-800 bg-opacity-75 transition-opacity">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
                <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                            {title}
                        </h3>

                        {description && (
                            <p className="text-sm text-gray-500 mb-4">{description}</p>
                        )}

                                <div className="relative h-64 w-full sm:h-80 bg-white rounded-md overflow-hidden">
                                    {imageSrc ? (
                                        <Cropper
                                            image={imageSrc}
                                            crop={crop}
                                            zoom={zoom}
                                            aspect={effectiveAspect}
                                            cropShape={cropShape}
                                            showGrid={false}
                                            restrictPosition={false}
                                            onCropChange={setCrop}
                                            onZoomChange={setZoom}
                                            onCropComplete={handleCropComplete}
                                        />
                                    ) : (
                                <div className="flex h-full w-full items-center justify-center text-sm text-gray-300">
                                    Carregando imagem...
                                </div>
                            )}
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Zoom
                            </label>
                            <input
                                type="range"
                                min={effectiveZoomRange.min}
                                max={effectiveZoomRange.max}
                                step={effectiveZoomRange.step}
                                value={zoom}
                                onChange={(event) => setZoom(Number(event.target.value))}
                                className="w-full accent-indigo-600"
                            />
                            <div className="mt-2 text-xs text-gray-500">
                                Ajuste o recorte para enquadrar a imagem. Arraste a imagem para reposicionar.
                            </div>
                        </div>

                        {error && (
                            <div className="mt-4 rounded-md bg-red-50 p-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        )}
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            onClick={handleConfirm}
                            className="btn-primary w-full sm:ml-3 sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={isProcessing}
                        >
                            {isProcessing ? 'Processando...' : confirmLabel}
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="btn-secondary-outline mt-3 w-full sm:mt-0 sm:w-auto"
                            disabled={isProcessing}
                        >
                            {cancelLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal;
